import React, { useEffect, useMemo, useRef, useState } from 'react';
import Ably from 'ably';
import { ChatClient, type ChatMessageEvent, type Room, Message, OrderBy } from '@ably/chat';
import { getSupabaseClient } from '../../lib/supabase';

type UiMessage = {
  id: string;
  text: string;
  time: string;
  timestamp: number;
  fromMe?: boolean;
  serial?: string;
  deleted?: boolean;
};

type ConnectionState = 'connecting' | 'connected' | 'failed';

type SupportChatModalProps = {
  onClose: () => void;
};

function Avatar({ fallback }: { fallback: string }) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/30 bg-[#E8DFBD]">
      <span className="text-sm font-medium text-[#2C2731]">{fallback}</span>
    </div>
  );
}

export default function SupportChatModal({ onClose }: SupportChatModalProps) {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState<ConnectionState>('connecting');
  const [occupancyCount, setOccupancyCount] = useState<number>(0);

  const ablyRef = useRef<Ably.Realtime | null>(null);
  const chatClientRef = useRef<ChatClient | null>(null);
  const roomRef = useRef<Room | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const supabase = useMemo(() => getSupabaseClient(), []);

  // Clear support notifications on mount
  useEffect(() => {
    (async () => {
      try {
        await supabase.functions.invoke('clear-support-notification', {
          body: {}
        });
      } catch (err) {
        console.warn('Failed to clear support notifications', err);
      }
    })();
  }, [supabase]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeConnection: (() => void) | undefined;
    let unsubscribeMessages: (() => void) | undefined;

    const serializeMessage = (
      input: ChatMessageEvent | Message
    ): UiMessage | null => {
      const msg = 'message' in input ? input.message : input;
      if (!msg) return null;

      const timestamp = msg.timestamp ?? Date.now();

      return {
        id: msg.id ?? `${timestamp}-${Math.random()}`,
        text: msg.text ?? '',
        time: new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        timestamp,
        fromMe: msg.clientId?.startsWith('seeker:'),
        serial: msg.serial,
        deleted: false,
      };
    };

    (async () => {
      try {
        setConnected('connecting');

        const { data, error } = await supabase.functions.invoke<{
          tokenRequest: Ably.Types.TokenRequest;
          roomName: string;
        }>('support-chat-token', { body: {} });

        if (error || !data) {
          throw error ?? new Error('Unable to obtain chat token');
        }

        if (cancelled) return;

        const { tokenRequest, roomName } = data;

        const ably = new Ably.Realtime({
          authCallback: (_params, cb) => cb(null, tokenRequest)
        });
        ablyRef.current = ably;

        const onConnectionChange = (stateChange: Ably.Types.ConnectionStateChange) => {
          if (stateChange.current === 'connected') {
            setConnected('connected');
          } else if (
            stateChange.current === 'failed' ||
            stateChange.current === 'closing' ||
            stateChange.current === 'closed' ||
            stateChange.current === 'suspended'
          ) {
            setConnected('failed');
          }
        };

        ably.connection.on(onConnectionChange);
        unsubscribeConnection = () => ably.connection.off(onConnectionChange);

        const chatClient = new ChatClient(ably);
        chatClientRef.current = chatClient;

        const room = await chatClient.rooms.get(roomName);
        if (cancelled) return;

        roomRef.current = room;
        await room.attach();

        try {
          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const history = await room.messages.history({
            start: sevenDaysAgo,
            orderBy: OrderBy.OldestFirst,
            limit: 200
          });
          if (!cancelled) {
            const historyMessages = history.items
              .map(serializeMessage)
              .filter((m): m is UiMessage => Boolean(m))
              .sort((a, b) => a.timestamp - b.timestamp);
            setMessages(historyMessages);
          }
        } catch (historyErr) {
          console.warn('Failed to load support chat history', historyErr);
        }

        const subscription = room.messages.subscribe((evt: ChatMessageEvent) => {
          const mapped = serializeMessage(evt);
          if (!mapped) return;
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === mapped.id)) {
              return prev;
            }
            const next = [...prev, mapped];
            next.sort((a, b) => a.timestamp - b.timestamp);
            return next;
          });
        });
        unsubscribeMessages = () => subscription.unsubscribe();

        await room.presence.enter({ role: 'seeker' });
        if (!cancelled) {
          setConnected('connected');
        }
      } catch (err) {
        console.error('Failed to initialise support chat', err);
        if (!cancelled) {
          setConnected('failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeConnection?.();
      unsubscribeMessages?.();

      (async () => {
        try {
          await roomRef.current?.presence.leave();
        } catch (err) {
          console.warn('Failed to leave presence', err);
        }
        try {
          if (roomRef.current) {
            await chatClientRef.current?.rooms.release(roomRef.current.name);
          }
        } catch (err) {
          console.warn('Failed to release room', err);
        }
        try {
          ablyRef.current?.close();
        } catch (err) {
          console.warn('Failed to close Ably connection', err);
        }
        roomRef.current = null;
        chatClientRef.current = null;
        ablyRef.current = null;
      })();
    };
  }, [supabase]);

  // Poll presence
  useEffect(() => {
    if (!roomRef.current || connected !== 'connected') return;

    let cancelled = false;

    const pollOccupancy = async () => {
      if (cancelled || !roomRef.current || connected !== 'connected') return;
      try {
        const presenceMembers = await roomRef.current?.presence.get();
        if (cancelled) return;
        setOccupancyCount(presenceMembers?.length ?? 0);
      } catch {
        if (cancelled) return;
        setOccupancyCount(1);
      }
    };

    pollOccupancy();
    const intervalId = setInterval(pollOccupancy, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [connected]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setInput('');
    try {
      await roomRef.current?.messages.send({ text: trimmed });

      // Always notify to keep conversation metadata updated
      try {
        await supabase.functions.invoke('support-message-notification', {
          body: {
            preview: trimmed.slice(0, 100),
            other_is_present: occupancyCount > 1,
          }
        });
      } catch (notifErr) {
        console.warn('Failed to send support notification', notifErr);
      }
    } catch (err) {
      console.error('Failed to send support chat message', err);
      setInput(trimmed);
    }
  };

  const messageNodes = useMemo(
    () =>
      messages.map((message) => {
        const isFromMe = message.fromMe;
        const isDeleted = message.deleted;
        return (
          <div
            key={message.id}
            className={`flex items-start gap-3 ${isFromMe ? 'flex-row-reverse' : ''}`}
          >
            <Avatar fallback={isFromMe ? 'You' : 'GC'} />
            <div className={`flex max-w-[314px] flex-col ${isFromMe ? 'items-end' : ''}`}>
              <div
                className={`w-full rounded-lg px-4 py-3 text-sm shadow-sm ${
                  isDeleted
                    ? 'bg-[#304031]/10 text-[#304031]/40 italic'
                    : isFromMe
                    ? 'bg-[#304031] text-[#FFFCEF]'
                    : 'bg-[#E8DFBD] text-[#2C2731]'
                }`}
              >
                {message.text}
              </div>
              <div className={`mt-2 flex items-center gap-2 text-xs ${
                isFromMe ? 'text-[#304031]/70' : 'text-[#2C2731]/70'
              }`}>
                <span>{message.time}</span>
              </div>
            </div>
          </div>
        );
      }),
    [messages]
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#FFFCEF]">
      <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
        <button
          type="button"
          aria-label="Back"
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
          onClick={onClose}
        >
          {'\u2190'}
        </button>

        <div className="flex flex-1 items-center justify-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E8DFBD] border border-[#2C2731]/30">
            <svg className="h-4 w-4 text-[#2C2731]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-base font-medium text-[#FFFCEF]">Support</span>
            <span className="text-xs text-[#FFFCEF]/70">
              {connected === 'connected'
                ? occupancyCount >= 2
                  ? 'Online now'
                  : 'Offline'
                : connected === 'connecting'
                ? 'Connecting...'
                : 'Offline'}
            </span>
          </div>
        </div>

        <div className="w-10" aria-hidden />
      </header>

      <main className="flex flex-1 min-h-0 flex-col">
        <div className="mx-auto flex h-full w-full max-w-2xl flex-1 min-h-0 flex-col gap-4 px-6 pb-6 pt-4">
          <div className="shrink-0 rounded-xl bg-[#304031] px-4 py-3 text-center text-sm font-medium text-[#FFFCEF] shadow-sm">
            Admin Support · Chat history kept for 7 days
          </div>

          <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm">
            <div ref={listRef} className="ph-no-capture flex-1 overflow-y-auto px-4 py-6">
              {connected === 'failed' ? (
                <p className="mx-auto mb-10 max-w-[324px] rounded-md bg-red-50 px-3 py-2 text-center text-sm text-red-600">
                  We couldn&apos;t connect to chat. Please try again.
                </p>
              ) : !messages.length ? (
                <p className="mx-auto mb-10 max-w-[324px] text-center text-sm text-[#2C2731]/70">
                  Need help? Send a message to the Guides Collective team.
                </p>
              ) : null}
              <div className="space-y-6">{messageNodes}</div>
            </div>

            <div className="border-t border-[#2C2731]/20 px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 rounded-lg bg-[#E8DFBD] px-4 py-2.5">
                  <input
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    placeholder="Type your message..."
                    className="w-full bg-transparent text-sm text-[#2C2731] placeholder:text-[#2C2731]/50 focus:outline-none"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    disabled={connected !== 'connected'}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={connected !== 'connected'}
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#304031] text-[#FFFCEF] hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#304031] disabled:opacity-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2 11 13" />
                    <path d="m22 2-7 20-2-7-7-13z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
